import { Helmet } from 'react-helmet-async';
import { BlogPostCard } from '../components/BlogPostCard';
import { useEffect, useState } from 'react';
import { blogService } from '../services/blogService';
import { BlogPost as BlogPostType } from '../models/Post';
import { PAGE_TITLES } from '../constants/pageTracking';
import { trackPageView } from '../utils/trackingUtils';

export default function Blog() {
    const [posts, setPosts] = useState<BlogPostType[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Track page view
    useEffect(() => {
        trackPageView('BLOG', 'blog');
    }, []);

    // Track section views
    const trackSectionView = (sectionName: string) => {
        if (typeof window.gtag !== 'undefined') {
            window.gtag('event', 'section_view', {
                page_title: PAGE_TITLES.BLOG.tracking,
                section_name: sectionName
            });
        }
    };

    // Track blog post interactions
    const trackPostInteraction = (postId: string, action: string) => {
        if (typeof window.gtag !== 'undefined') {
            window.gtag('event', 'blog_interaction', {
                page_title: PAGE_TITLES.BLOG.tracking,
                post_id: postId,
                action: action
            });
        }
    };

    // Section tracking with Intersection Observer
    useEffect(() => {
        const sections = [
            { id: 'blog-header', name: 'Blog Header' },
            { id: 'blog-posts', name: 'Blog Posts List' },
            { id: 'blog-search', name: 'Blog Search' }
        ];

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        const sectionName = sections.find(s => s.id === entry.target.id)?.name;
                        if (sectionName) {
                            trackSectionView(sectionName);
                        }
                    }
                });
            },
            { threshold: 0.5 }
        );

        sections.forEach(section => {
            const element = document.getElementById(section.id);
            if (element) observer.observe(element);
        });

        return () => observer.disconnect();
    }, []);

    // Fetch posts
    useEffect(() => {
        const fetchPosts = async () => {
            try {
                setLoading(true);
                const allPosts = await blogService.getPosts();
                const publishedPosts = allPosts.posts.filter(post => {
                    if (typeof post.Status === 'number') {
                        return post.Status === 1;
                    }
                    return post.Status === 'published';
                });
                setPosts(publishedPosts);
            } catch (err) {
                console.error('Failed to fetch blog posts:', err);
                setError('Failed to load blog posts. Please try again later.');
            } finally {
                setLoading(false);
            }
        };

        fetchPosts();
    }, []);

    // Add back the structured data
    const structuredData = {
        "@context": "https://schema.org",
        "@type": "Blog",
        "name": "EmploSkills Blog",
        "description": "Insights, tips, and updates from the world of business education",
        "url": "https://homeschool.emploskills.com/blog",
        "blogPost": posts.map(post => ({
            "@type": "BlogPosting",
            "headline": post.Title,
            "datePublished": post.CreatedAt,
            "author": {
                "@type": "Person",
                "name": post.Author.Name
            },
            "image": post.ImageUrl,
            "description": post.Excerpt
        }))
    };

    return (
        <>
            <Helmet>
                <title>{PAGE_TITLES.BLOG.seo}</title>
                <meta 
                    name="description" 
                    content="Discover practical insights and tips about business education, financial literacy, and teaching methodologies for homeschool and vocational students." 
                />
                <meta 
                    name="keywords" 
                    content="business education, financial literacy, homeschool teaching, educational blog, business skills" 
                />
                
                {/* Open Graph */}
                <meta property="og:title" content="EmploSkills Blog - Business Education Insights" />
                <meta 
                    property="og:description" 
                    content="Discover practical insights about business education and financial literacy for homeschool and vocational students." 
                />
                <meta property="og:type" content="blog" />
                <meta property="og:url" content="https://homeschool.emploskills.com/blog" />
                
                {/* Structured Data */}
                <script type="application/ld+json">
                    {JSON.stringify(structuredData)}
                </script>
                <link rel="canonical" href="https://homeschool.emploskills.com/blog" />
            </Helmet>

            <main className="min-h-screen bg-gray-50 py-12">
                <div className="max-w-6xl mx-auto px-4">
                    <header id="blog-header" className="text-center mb-12">
                        <h1 className="text-4xl font-bold text-gray-900 mb-4">
                            EmploSkills Blog
                        </h1>
                        <p className="text-xl text-gray-600">
                            Insights, tips, and updates from the world of business education
                        </p>
                    </header>

                    {/* Search Section */}
                    {!loading && !error && (
                        <div id="blog-search" className="mb-8">
                            <input
                                type="search"
                                placeholder="Search articles..."
                                aria-label="Search blog posts"
                                className="w-full max-w-md px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary focus:border-transparent"
                                onChange={(e) => {
                                    if (typeof window.gtag !== 'undefined') {
                                        window.gtag('event', 'blog_search', {
                                            search_term: e.target.value
                                        });
                                    }
                                }}
                            />
                        </div>
                    )}

                    {/* Blog Posts Grid */}
                    {!loading && !error && (
                        <div id="blog-posts" className="space-y-8" role="feed" aria-label="Blog posts">
                            {posts.map((post) => (
                                <div key={post.Id} onClick={() => trackPostInteraction(post.Id, 'view')}>
                                    <BlogPostCard {...post} />
                                </div>
                            ))}
                            {posts.length === 0 && (
                                <p className="text-center text-gray-600 py-12">
                                    No blog posts available at the moment.
                                </p>
                            )}
                        </div>
                    )}
                </div>
            </main>
        </>
    );
}